import {
    Badge,
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    tableCellClasses,
    TablePagination
} from "@mui/material";
import React, {useEffect, useRef, useState} from "react";
import {useTheme} from '@mui/material/styles';
import ExpandLessIcon from '@mui/icons-material/ExpandLessOutlined';
import ExpandMorIcon from '@mui/icons-material/ExpandMoreOutlined';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMoreOutlined';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
    getFilteredRowModel,
    getPaginationRowModel
} from '@tanstack/react-table'
import {tokens} from "../theme";
import FilterListIcon from '@mui/icons-material/FilterList';
import TableFilterDialog from './TableFilterDialog';
import {
    SecondaryOutlinedButton,
    StyledHeadCell,
    StyledSortIcon,
    StyledTableCell,
    StyledTableRow,
    StyledTableRowHead
} from "./Styles";
import ActiveFilters from './ActiveFilters';

const ListViewV2 = ({
                        columns,
                        dispatchData,
                        tableName,
                        columnFilters,
                        setColumnFilters,
                        applyFilters,
                        pagination,
                        setQueryParams,
                        queryParams,
                        existingFilters = true,
                        existingPagination = true,
                        debouncedSearchValue,
                        isFilterOpen,
                        setIsFilterOpen,
                        getCellProps
                    }) => {

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [data, setData] = useState(dispatchData);
    const [sorting, setSorting] = useState(() => {
        if (queryParams?.sorting) {
            try {
                const sortingInfo = JSON.parse(queryParams.sorting);
                if (sortingInfo && sortingInfo.column) {
                    return [{ id: sortingInfo.column, desc: sortingInfo.direction === 'desc' }];
                }
            } catch (e) {
                // ignore malformed sorting
            }
        }
        return [];
    });

    // console.log(pagination);
    const [page, setPage] = useState(pagination?.currentPage - 1 ||pagination?.page || 0);

    const [rowsPerPage, setRowsPerPage] = useState(() => {
        const savedRowsPerPage = localStorage.getItem(`${tableName}-rowsPerPage`);
        if (savedRowsPerPage !== null) {
            return parseInt(savedRowsPerPage, 10);
        }
        return pagination?.rowsPerPage || 10;
    });

    const handleChangePage = (event, newPage) => {
        setPage(newPage);

        // Get current sorting info
        const sortingInfo = sorting && sorting.length > 0
            ? {
                column: sorting[0].id,
                direction: sorting[0].desc ? 'desc' : 'asc'
            }
            : null;

        applyFilters(columnFilters, {
            currentPage: newPage + 1,
            rowsPerPage: rowsPerPage,
        }, sortingInfo);  // Pass the current sorting info
    };

    useEffect(() => {
        setData(dispatchData);
        setPage(pagination?.currentPage - 1 ||pagination?.page -1 || 0)
    }, [dispatchData, pagination]);

    // Apply initial server-side sorting when component mounts
    useEffect(() => {
        if (queryParams?.sorting) {
            try {
                const sortingInfo = JSON.parse(queryParams.sorting);
                if (sortingInfo && sortingInfo.column) {
                    applyFilters(
                        columnFilters,
                        {
                            currentPage: page + 1,
                            rowsPerPage: rowsPerPage
                        },
                        {
                            column: sortingInfo.column,
                            direction: sortingInfo.direction
                        }
                    );
                }
            } catch (e) {
                // ignore initial malformed sorting
            }
        }
    }, []);

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);

        // Save to localStorage
        localStorage.setItem(`${tableName}-rowsPerPage`, newRowsPerPage.toString());

        // Get current sorting info
        const sortingInfo = sorting && sorting.length > 0
            ? {
                column: sorting[0].id,
                direction: sorting[0].desc ? 'desc' : 'asc'
            }
            : null;

        applyFilters(
            columnFilters,
            {
                currentPage: 1,
                rowsPerPage: newRowsPerPage,
            },
            sortingInfo
        );
    };


    // Handle column sorting changes
    const handleSortingChange = (updatedSorting) => {
        // Convert function-based updates to actual sorting value
        const newSorting = typeof updatedSorting === 'function'
            ? updatedSorting(sorting)
            : updatedSorting;

        setSorting(newSorting);

        // Create sorting info object for the API
        const sortingInfo = newSorting && newSorting.length > 0
            ? {
                column: newSorting[0].id,
                direction: newSorting[0].desc ? 'desc' : 'asc'
            }
            : null;

        // Reset to first page if sorting a different column
        let currentSortingInfo = null;
        if (queryParams?.sorting) {
            try {
                currentSortingInfo = JSON.parse(queryParams.sorting);
            } catch (e) {
                currentSortingInfo = null;
            }
        }

        const newPage = currentSortingInfo && currentSortingInfo.column !== sortingInfo?.column ? 0 : page;

        // Update page state if needed
        if (newPage !== page) {
            setPage(newPage);
        }

        // Apply server-side filtering with updated sorting
        applyFilters(
            columnFilters,
            {
                currentPage: newPage + 1,
                rowsPerPage: rowsPerPage
            },
            sortingInfo
        );

        // Update URL query parameters with new sorting and pagination
        setQueryParams(prev => ({
            ...prev,
            sorting: sortingInfo ? JSON.stringify(sortingInfo) : "",
            pagination: JSON.stringify({
                currentPage: newPage + 1,
                rowsPerPage: rowsPerPage
            })
        }));
    };

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            columnFilters,
            pagination: {
                pageIndex: pagination?.currentPage - 1 || page,
                pageSize: pagination?.rowsPerPage || rowsPerPage,
            },
        },
        manualPagination: true,
        pageCount: pagination?.totalPages || Math.ceil((pagination?.total || 0) / rowsPerPage),
        enableSorting: true,
        enableMultiSort: false,
        onSortingChange: handleSortingChange,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        debugTable: true,
    });


    // Handle search with debounce (skip on initial mount)
    const didInitSearchRef = useRef(false);
    useEffect(() => {
        if (!didInitSearchRef.current) {
            didInitSearchRef.current = true;
            return;
        }
        // Apply filters with the debounced value
        const sortingInfo = sorting && sorting.length > 0
            ? { column: sorting[0].id, direction: sorting[0].desc ? 'desc' : 'asc' }
            : null;
        applyFilters(
            columnFilters,
            {
                currentPage: 1,
                rowsPerPage: rowsPerPage,
            },
            sortingInfo,
            debouncedSearchValue
        );

    }, [debouncedSearchValue]);

    // console.log({
    //     page,
    //     rowsPerPage,
    //     count: pagination?.total
    // });


    return (
        <div key={`ListView`}>
            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    flexDirection: {xs: 'column', sm: 'row'},
                    alignItems: {xs: 'stretch', sm: 'center'},
                    gap: 2,
                    mb: 2
                }}
                key={`ListView-Box`}
            >
                <Box sx={{
                    width: {xs: '100%', sm: '100%'},
                    display: 'flex',
                    justifyContent: 'flex-start',
                    marginTop: 2
                }}>
                </Box>
            </Box>
            {existingFilters &&
                <ActiveFilters
                    columnFilters={columnFilters}
                    columns={columns}
                    setColumnFilters={setColumnFilters}
                    colors={colors}
                    applyFilters={applyFilters}
                    key={`ActiveFilters`}
                />
            }
            {existingFilters &&
                <TableFilterDialog
                    open={isFilterOpen}
                    onClose={() => setIsFilterOpen(false)}
                    columns={columns}
                    setColumnFilters={setColumnFilters}
                    columnFilters={columnFilters}
                    applyFilters={applyFilters}
                    tableName={tableName}
                    key={`TableFilterDialog`}
                />
            }
            {data.length ? (
                <Box sx={{marginTop: "10px"}} key={`ListView-Box`}>
                    <TableContainer component={Paper} key='reactTableContainer'>
                        <Table aria-label="simple table" key={`ListView-Table`}>
                            <TableHead key={`ListView-TableHead`}>
                                {table.getHeaderGroups().map(headerGroup => (
                                    <StyledTableRowHead key={headerGroup.id}>
                                        {headerGroup.headers.map(header => {
                                            return (
                                                <StyledHeadCell
                                                    colors={colors}
                                                    key={header.id}
                                                    colSpan={header.colSpan}
                                                >
                                                    {header.isPlaceholder ? null : (
                                                        <Box
                                                            {...{
                                                                style: header.column.getCanSort()
                                                                    ? {cursor: 'pointer', userSelect: 'none'}
                                                                    : {},
                                                                onClick: header.column.getToggleSortingHandler(),
                                                            }}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                color: header.column.getIsSorted() ? colors.primary[500] : 'inherit',
                                                            }}
                                                        >
                                                            {flexRender(
                                                                header.column.columnDef.header,
                                                                header.getContext()
                                                            )}
                                                            {header.column.getCanSort() && (
                                                                <StyledSortIcon>
                                                                    {header.column.getIsSorted() ? (
                                                                        header.column.getIsSorted() === 'asc' ? (
                                                                            <ExpandLessIcon/>
                                                                        ) : (
                                                                            <ExpandMorIcon/>
                                                                        )
                                                                    ) : (
                                                                        <UnfoldMoreIcon sx={{opacity: 0.2}}/>
                                                                    )}
                                                                </StyledSortIcon>
                                                            )}
                                                        </Box>
                                                    )}
                                                </StyledHeadCell>
                                            )
                                        })}
                                    </StyledTableRowHead>
                                ))}
                            </TableHead>
                            <TableBody key={`ListView-TableBody`}>
                                {table.getRowModel().rows.map(row => {
                                    return (
                                        <StyledTableRow key={row.id}>
                                            {row.getVisibleCells().map(cell => {
                                                const extraCellProps = typeof getCellProps === 'function' ? (getCellProps(cell) || {}) : {};
                                                if (extraCellProps.skip) {
                                                    return null;
                                                }
                                                return (
                                                    <StyledTableCell key={cell.id} colors={colors} {...extraCellProps}>
                                                        {flexRender(
                                                            cell.column.columnDef.cell,
                                                            cell.getContext()
                                                        )}
                                                    </StyledTableCell>
                                                )
                                            })}
                                        </StyledTableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                        {existingPagination && (
                            <TablePagination
                                key={`ListView-TablePagination`}
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                component="div"
                                count={pagination?.total || 0}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelRowsPerPage="Lignes par page :"
                                labelDisplayedRows={({from, to, count}) => {
                                    return `Page ${pagination?.currentPage || page + 1} sur ${pagination?.totalPages || Math.ceil(count / rowsPerPage)}`;
                                }}
                                showFirstButton
                                showLastButton
                                getItemAriaLabel={(type) => {
                                    if (type === 'first') {
                                        return 'Première page';
                                    }
                                    if (type === 'last') {
                                        return 'Dernière page';
                                    }
                                    if (type === 'next') {
                                        return 'Page suivante';
                                    }
                                    return 'Page précédente';
                                }}
                                sx={{
                                    borderTop: `1px solid ${colors.grey[200]}`,
                                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                                        color: colors.grey[500],
                                    },
                                }}
                            />
                        )}
                    </TableContainer>
                </Box>
            ) : (
                <Typography marginY={2} textAlign="center" key={`ListView-Typography`}> No data
                    available</Typography>
            )}
        </div>
    );
};
export default ListViewV2;